import { supabaseClient } from '../supabase';
import { logger } from '../logger';
import { HOTEL_ID } from '../supabase/configClient';
import { v4 as uuidv4 } from 'uuid';
import type { SupportedLanguages } from '../../i18n';

interface SessionData {
  id: string;
  hotel_id: string;
  anonymous_id: string;
  start_time: string;
  device_type?: string;
  browser?: string;
  os?: string;
  screen_size?: string;
  is_pwa: boolean;
  is_returning_user: boolean;
  referrer?: string;
  country?: string;
  region?: string;
  city?: string;
  timezone?: string;
  language?: string;
  ip_address?: string;
  is_active: boolean;
  page_views: number;
  interactions: number;
  device_id?: string;
  app_version?: string;
  network_type?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

class SessionTracker {
  private static instance: SessionTracker;
  private sessionId: string | null = null;
  private anonymousId: string | null = null;
  private sessionData: SessionData | null = null;
  private pageViews: number = 0;
  private interactions: number = 0;
  private isInitialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): SessionTracker {
    if (!SessionTracker.instance) {
      SessionTracker.instance = new SessionTracker();
    }
    return SessionTracker.instance;
  }

  private async initialize(): Promise<void> {
    if (this.isInitialized || this.initPromise) {
      return this.initPromise || Promise.resolve();
    }

    this.initPromise = (async () => {
      try {
        logger.debug('Initialisation du tracker de session', {
          category: 'Analytics',
          data: { timestamp: new Date().toISOString() }
        });

        // Récupérer ou générer l'ID anonyme
        this.anonymousId = this.getOrCreateAnonymousId();

        // Créer une nouvelle session
        await this.createSession();

        this.isInitialized = true;
        logger.debug('Tracker de session initialisé avec succès', {
          category: 'Analytics',
          data: { 
            sessionId: this.sessionId,
            anonymousId: this.anonymousId
          }
        });
      } catch (error) {
        logger.error('Erreur lors de l\'initialisation du tracker de session', {
          category: 'Analytics',
          data: { error }
        });
        // Ne pas marquer comme initialisé en cas d'erreur
        this.isInitialized = false;
      } finally {
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  private getOrCreateAnonymousId(): string {
    try {
      // Essayer de récupérer l'ID anonyme du localStorage
      let anonymousId = localStorage.getItem('anonymous_id');
      
      // Si l'ID n'existe pas, en créer un nouveau
      if (!anonymousId) {
        anonymousId = uuidv4();
        localStorage.setItem('anonymous_id', anonymousId);
        logger.debug('Nouvel ID anonyme créé', {
          category: 'Analytics',
          data: { anonymousId }
        });
      } else {
        logger.debug('ID anonyme existant récupéré', {
          category: 'Analytics',
          data: { anonymousId }
        });
      }
      
      return anonymousId;
    } catch (error) {
      // En cas d'erreur, générer un ID temporaire
      logger.error('Erreur lors de la récupération/création de l\'ID anonyme', {
        category: 'Analytics',
        data: { error }
      });
      return uuidv4();
    }
  }

  private async createSession(): Promise<void> {
    try {
      // Générer un nouvel ID de session
      this.sessionId = uuidv4();
      
      // Collecter les données de la session
      this.sessionData = {
        id: this.sessionId,
        hotel_id: HOTEL_ID,
        anonymous_id: this.anonymousId!,
        start_time: new Date().toISOString(),
        device_type: this.getDeviceType(),
        browser: this.getBrowser(),
        os: this.getOS(),
        screen_size: this.getScreenSize(),
        is_pwa: this.isPWA(),
        is_returning_user: this.isReturningUser(),
        referrer: document.referrer || undefined,
        language: navigator.language,
        is_active: true,
        page_views: 1,
        interactions: 0
      };
      
      // Ajouter les paramètres UTM si présents
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('utm_source')) {
        this.sessionData.utm_source = urlParams.get('utm_source') || undefined;
      }
      if (urlParams.has('utm_medium')) {
        this.sessionData.utm_medium = urlParams.get('utm_medium') || undefined;
      }
      if (urlParams.has('utm_campaign')) {
        this.sessionData.utm_campaign = urlParams.get('utm_campaign') || undefined;
      }
      
      // Insérer la session dans la base de données
      const { error } = await supabaseClient
        .from('sessions')
        .insert([this.sessionData]);
      
      if (error) {
        throw error;
      }
      
      // Initialiser les compteurs
      this.pageViews = 1;
      this.interactions = 0;
      
      logger.debug('Nouvelle session créée', {
        category: 'Analytics',
        data: { 
          sessionId: this.sessionId,
          anonymousId: this.anonymousId,
          isPWA: this.sessionData.is_pwa,
          isReturningUser: this.sessionData.is_returning_user
        }
      });
      
      // Ajouter un gestionnaire pour mettre à jour la session lors de la fermeture de la page
      window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
      
    } catch (error) {
      logger.error('Erreur lors de la création de la session', {
        category: 'Analytics',
        data: { error }
      });
      throw error;
    }
  }

  private async updateSession(): Promise<void> {
    if (!this.sessionId || !this.sessionData) {
      logger.warn('Tentative de mise à jour d\'une session non initialisée', {
        category: 'Analytics'
      });
      return;
    }
    
    try {
      // Mettre à jour les compteurs dans les données de session
      this.sessionData.page_views = this.pageViews;
      this.sessionData.interactions = this.interactions;
      
      // Mettre à jour la session dans la base de données
      const { error } = await supabaseClient
        .from('sessions')
        .update({
          page_views: this.pageViews,
          interactions: this.interactions,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.sessionId);
      
      if (error) {
        throw error;
      }
      
      logger.debug('Session mise à jour', {
        category: 'Analytics',
        data: { 
          sessionId: this.sessionId,
          pageViews: this.pageViews,
          interactions: this.interactions
        }
      });
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de la session', {
        category: 'Analytics',
        data: { error }
      });
    }
  }

  private async handleBeforeUnload(): Promise<void> {
    if (!this.sessionId || !this.sessionData) return;
    
    try {
      // Calculer la durée de la session
      const startTime = new Date(this.sessionData.start_time).getTime();
      const endTime = Date.now();
      const duration = Math.floor((endTime - startTime) / 1000); // en secondes
      
      // Mettre à jour la session avec la durée et marquer comme inactive
      await supabaseClient
        .from('sessions')
        .update({
          is_active: false,
          duration,
          end_reason: 'page_close',
          page_views: this.pageViews,
          interactions: this.interactions,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.sessionId);
      
    } catch (error) {
      // Ne pas logger l'erreur car beforeunload ne permet pas d'attendre la fin des opérations asynchrones
    }
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  private getBrowser(): string {
    const ua = navigator.userAgent;
    
    if (ua.indexOf("Firefox") > -1) {
      return "Firefox";
    } else if (ua.indexOf("SamsungBrowser") > -1) {
      return "Samsung Browser";
    } else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) {
      return "Opera";
    } else if (ua.indexOf("Edg") > -1) {
      return "Edge";
    } else if (ua.indexOf("Chrome") > -1) {
      return "Chrome";
    } else if (ua.indexOf("Safari") > -1) {
      return "Safari";
    } else {
      return "Unknown";
    }
  }

  private getOS(): string {
    const ua = navigator.userAgent;
    
    if (/Windows/.test(ua)) {
      return "Windows";
    } else if (/iPhone|iPad|iPod/.test(ua)) {
      return "iOS";
    } else if (/Android/.test(ua)) {
      return "Android";
    } else if (/Mac OS X/.test(ua)) {
      return "macOS";
    } else if (/Linux/.test(ua)) {
      return "Linux";
    } else {
      return "Unknown";
    }
  }

  private getScreenSize(): string {
    return `${window.screen.width}x${window.screen.height}`;
  }

  private isPWA(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || 
           (navigator as any).standalone === true;
  }

  private isReturningUser(): boolean {
    try {
      return localStorage.getItem('has_visited') === 'true';
    } catch {
      return false;
    }
  }

  public getSessionId(): string | null {
    return this.sessionId;
  }

  public getAnonymousId(): string | null {
    return this.anonymousId;
  }

  public incrementPageView(): void {
    this.pageViews++;
    
    // Mettre à jour la session toutes les 5 vues de page
    if (this.pageViews % 5 === 0) {
      this.updateSession();
    }
  }

  public incrementInteraction(): void {
    this.interactions++;
    
    // Mettre à jour la session toutes les 10 interactions
    if (this.interactions % 10 === 0) {
      this.updateSession();
    }
  }

  public updateLanguage(language: SupportedLanguages): void {
    if (!this.sessionId || !this.sessionData) return;
    
    try {
      // Mettre à jour la langue dans les données de session
      this.sessionData.language = language;
      
      // Mettre à jour la session dans la base de données
      supabaseClient
        .from('sessions')
        .update({
          language,
          updated_at: new Date().toISOString()
        })
        .eq('id', this.sessionId)
        .then(({ error }) => {
          if (error) {
            throw error;
          }
          
          logger.debug('Langue de session mise à jour', {
            category: 'Analytics',
            data: { 
              sessionId: this.sessionId,
              language
            }
          });
        })
        .catch(error => {
          logger.error('Erreur lors de la mise à jour de la langue de session', {
            category: 'Analytics',
            data: { error }
          });
        });
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de la langue de session', {
        category: 'Analytics',
        data: { error }
      });
    }
  }
}

export const sessionTracker = SessionTracker.getInstance();